import * as React from 'react';
import { Grid, makeStyles, Tooltip, FilledInput } from '@material-ui/core';
import { useRouter } from 'next/router';
import debounce from 'lodash/debounce';
import clsx from 'clsx';
import { Typography } from '@swagup-com/components';
import { Search } from '@material-ui/icons';
import UserAvatar from './UserAvatar';
import { joinFields } from '../../helpers/utils';
import { scrollBar, ellipsisStyles } from '../shared/styles/commonStyles';
import { Link } from '../shared';
import NewBadge from '../shared/NewBadge';

const commonHover = {
  '&:hover': {
    cursor: 'pointer',
    backgroundColor: '#00000014'
  }
};
const useStyles = makeStyles({
  profileName: { color: '#131415', fontSize: 14 },
  swapAccountItem: { ...commonHover, padding: '8px 12px' },
  accountControlItem: { ...commonHover, padding: '12px 16px' },
  labelItem: { marginLeft: 10, color: '#4A4F54' },
  profileEmail: { color: '#989EA4' },
  rowItem: { padding: '12px 16px', borderBottom: '1px solid #E5E7E8' },
  currentAccount: { padding: '8px 16px', borderBottom: '1px solid #E5E7E8' },
  accountsContainer: { ...scrollBar, maxHeight: 300, borderTop: '1px solid #E5E7E8' },
  selected: { backgroundColor: '#3577d414' },
  border: { marginTop: 8, borderTop: '1px solid #E5E7E8' },
  helpMargin: { marginTop: 8 },
  noMargin: { marginLeft: 0 },
  accountsEmptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
    minHeight: 90
  },
  tooltip: {
    width: 220,
    borderRadius: '4px 0px 4px 4px',
    padding: '0px 0px 8px 0px',
    '&.MuiTooltip-tooltipPlacementLeft': { margin: 0 }
  }
});

const useSearchFieldStyles = makeStyles({
  root: {
    background: 'transparent',
    height: 48,
    fontSize: 12,
    '&:hover': { background: 'transparent' },
    '&:focus-within': { background: 'transparent' },
    '& .MuiSvgIcon-root': { color: '#4A4F54', width: 18, marginTop: 3 }
  },
  input: {
    ...ellipsisStyles,
    padding: '10px 10px 10px 8px',
    background: 'transparent'
  }
});

const Profile = ({ profile, classes }) => {
  const name = joinFields([profile.first_name, profile.last_name], ' ');
  const { currentAccount } = profile;

  return (
    <Grid container alignItems="center" className={classes.rowItem}>
      <Grid item xs={2}>
        <UserAvatar name={name} nameForColorGeneration={currentAccount?.name} size={32} fontSize={16} />
      </Grid>
      <Grid item xs={10} style={{ paddingLeft: 5 }}>
        <Typography variant="body4SemiBoldInter" className={classes.profileName}>
          {name}
        </Typography>
        <Typography variant="body4RegularInter" className={classes.profileEmail}>
          {profile.email}
        </Typography>
      </Grid>
    </Grid>
  );
};

const CurrentAccount = ({ accountName, classes }) => (
  <Grid container alignItems="center" className={classes.currentAccount}>
    <Grid item xs={12}>
      <Typography variant="body4RegularInter" style={{ color: '#989EA4' }}>
        Current Account
      </Typography>
    </Grid>
    <Grid item xs={12} style={{ marginTop: 4 }}>
      <Typography variant="body4MediumInter">{accountName}</Typography>
    </Grid>
  </Grid>
);

const SwapAccountItem = ({ companyName, email, onSwapAccount, classes }) => (
  <Grid container alignItems="center" onClick={onSwapAccount} className={classes.swapAccountItem}>
    <Grid item xs={2}>
      <UserAvatar name={companyName} nameForColorGeneration={companyName} size={32} />
    </Grid>
    <Grid item xs={10} style={{ paddingLeft: 8 }}>
      <Typography variant="body4SemiBoldInter" style={{ color: '#131415' }}>
        {companyName}
      </Typography>
      <Typography variant="body4RegularInter" className={classes.profileEmail}>
        {email}
      </Typography>
    </Grid>
  </Grid>
);

const SearchField = ({ onChange }) => {
  const classes = useSearchFieldStyles();

  return (
    <FilledInput
      id="account-search-input"
      onChange={e => onChange(e.target.value)}
      placeholder="Search Accounts"
      startAdornment={<Search />}
      fullWidth
      autoFocus
      disableUnderline
      classes={classes}
    />
  );
};

const SwapAccounts = ({ profile, onSwapAccount, classes }) => {
  const [searchValue, setSearchValue] = React.useState('');
  const [open, setOpen] = React.useState(false);

  const debouncedSearch = React.useCallback(
    debounce(value => setSearchValue(value), 500),
    [setSearchValue]
  );

  const accounts = profile.related_accounts?.filter(a => a.id !== profile.current_account);
  if (accounts?.length === 0) return null;

  const filteredAccounts = accounts?.filter(a => a.name.toLowerCase().includes(searchValue.toLowerCase()));

  const title = (
    <>
      <SearchField onChange={debouncedSearch} />
      <Grid container className={classes.accountsContainer}>
        {filteredAccounts?.length === 0 ? (
          <Grid container className={classes.accountsEmptyContainer}>
            <Typography variant="body4SemiBoldInter">No Account Found</Typography>
            <Typography variant="body4RegularInter" style={{ textAlign: 'center', color: '#989EA4' }}>
              Remove filter to see all the accounts.
            </Typography>
          </Grid>
        ) : (
          filteredAccounts?.map(a => (
            <SwapAccountItem
              key={a.id}
              companyName={a.name}
              email={profile.email}
              onSwapAccount={() => onSwapAccount(a.id)}
              classes={classes}
            />
          ))
        )}
      </Grid>
    </>
  );

  return (
    <Tooltip
      disableTouchListener
      disableHoverListener
      interactive
      placement="left-start"
      title={title}
      open={open}
      classes={{ tooltip: classes.tooltip }}
    >
      <Grid
        container
        alignItems="center"
        onClick={() => setOpen(true)}
        className={clsx(classes.accountControlItem, open && classes.selected)}
      >
        <img src="/images/public/switch-accounts.svg" alt="Switch Accounts" />
        <Typography variant="body3RegularInter" className={classes.labelItem}>
          Switch Accounts
        </Typography>
      </Grid>
    </Tooltip>
  );
};

const HelpCenter = ({ loggedOut, classes }) => (
  <a href="https://support.swagup.com" target="_blank" rel="noopener noreferrer">
    <Grid container className={clsx(loggedOut && classes.border)}>
      <Grid container alignItems="center" className={clsx(classes.accountControlItem, loggedOut && classes.helpMargin)}>
        <img src="/images/public/help-center.svg" alt="Help Center" />
        <Typography variant="body3RegularInter" className={classes.labelItem}>
          Help Center
        </Typography>
      </Grid>
    </Grid>
  </a>
);

const ReferFriend = ({ loggedOut, classes }) => (
  <a href="https://launch.swagup.com/refer" target="_blank" rel="noopener noreferrer">
    <Grid container className={clsx(loggedOut && classes.border)}>
      <Grid container alignItems="center" className={clsx(classes.accountControlItem, loggedOut && classes.helpMargin)}>
        <img src="/images/public/refer-friend.svg" alt="Help Center" />
        <NewBadge size="small">
          <Typography variant="body3RegularInter" className={classes.labelItem}>
            Refer a Friend
          </Typography>
        </NewBadge>
      </Grid>
    </Grid>
  </a>
);

const MenuItem = ({ itemName, to, classes }) => {
  const isSSR = useRouter();
  if (!isSSR && itemName === 'Dashboard') return null;

  const imageSrcName = itemName.toLowerCase().replace(/ /g, '-');

  return (
    <Link to={to}>
      <Grid container alignItems="center" className={classes.accountControlItem}>
        <img src={`/images/public/${imageSrcName}.svg`} alt={imageSrcName} />
        <Typography variant="body3RegularInter" className={classes.labelItem}>
          {itemName}
        </Typography>
      </Grid>
    </Link>
  );
};

const MenuAuthItem = ({ itemName, onClick, classes }) => {
  const loggedOut = itemName === 'Log Out';

  return (
    <Grid container alignItems="center" onClick={() => onClick()} className={classes.accountControlItem}>
      {loggedOut && <img src="/images/public/log-out.svg" alt="Log Out" />}
      <Typography variant="body3RegularInter" className={clsx(classes.labelItem, !loggedOut && classes.noMargin)}>
        {itemName}
      </Typography>
    </Grid>
  );
};

const AccountControl = ({ profile, onSwapAccount, logout, login }) => {
  const classes = useStyles();

  return profile ? (
    <>
      <Profile profile={profile} classes={classes} />
      <CurrentAccount accountName={profile.currentAccount?.name} classes={classes} />
      <MenuItem itemName="Dashboard" to="/" classes={classes} />
      <MenuItem itemName="Account Settings" to="/account/profile" classes={classes} />
      <SwapAccounts profile={profile} onSwapAccount={onSwapAccount} classes={classes} />
      <ReferFriend classes={classes} />
      <HelpCenter classes={classes} />
      <MenuAuthItem itemName="Log Out" onClick={logout} classes={classes} />
    </>
  ) : (
    <>
      <MenuAuthItem itemName="Sign Up" onClick={login} classes={classes} />
      <MenuAuthItem itemName="Log In" onClick={login} classes={classes} />
      <HelpCenter loggedOut classes={classes} />
    </>
  );
};

export default AccountControl;
